@if (step() === 4) {
  <!-- ── Step 4 · Review & launch ─────────────────────────────────────────── -->
  <section class="mx-auto flex w-full max-w-4xl flex-col gap-y-6">
    <header class="flex flex-row items-start justify-between gap-x-6">
      <div class="flex flex-col gap-y-1">
        <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">
          {{ hasLaunched() ? 'Live · submitted to TikTok' : 'Final check' }}
        </p>
        <h1 class="m-0 text-2xl font-bold text-primary">
          {{ hasLaunched() ? 'You’re live. Turn up the volume.' : 'Everything look right?' }}
        </h1>
        <p class="m-0 max-w-xl text-sm text-secondary">
          @if (hasLaunched()) {
            Your Spark ad is with TikTok for approval. Attribution flows straight into your ads
            dashboard once it starts delivering.
          } @else {
            Three levels, one screen. Change anything here — nothing spends until you launch.
          }
        </p>
      </div>

      @if (hasLaunched()) {
        <div
          aria-hidden="true"
          class="flex size-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-studio-dark to-brand-studio text-2xl font-bold text-white shadow-lg"
        >
          ✓
        </div>
      }
    </header>

    <!-- Summary cards -->
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <article class="flex flex-col gap-y-2 rounded-xl border border-border bg-card p-5">
        <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio-dark uppercase">
          1 · Campaign
        </p>
        <h2 class="m-0 text-base font-bold text-primary">{{ wizard.campaignName() }}</h2>
        <p class="m-0 text-sm text-secondary">{{ objectiveLabel() }}</p>
        @if (!hasLaunched()) {
          <button
            type="button"
            (click)="goToStep(1)"
            class="mt-auto cursor-pointer self-start text-sm font-semibold text-brand-studio hover:underline"
          >
            Edit
          </button>
        }
      </article>

      <article class="flex flex-col gap-y-2 rounded-xl border border-border bg-card p-5">
        <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio-dark uppercase">
          2 · Ad group
        </p>
        <h2 class="m-0 text-base font-bold text-primary">{{ wizard.adGroupName() }}</h2>
        <p class="m-0 text-sm text-secondary">{{ summaryBudget() }}</p>
        @if (!hasLaunched()) {
          <button
            type="button"
            (click)="goToStep(2)"
            class="mt-auto cursor-pointer self-start text-sm font-semibold text-brand-studio hover:underline"
          >
            Edit
          </button>
        }
      </article>

      <article class="flex flex-col gap-y-2 rounded-xl border border-border bg-card p-5">
        <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio-dark uppercase">
          3 · Spark ad
        </p>
        <h2 class="m-0 text-base font-bold text-primary">
          {{ selectedVideo()?.handle ?? 'No post chosen' }}
        </h2>
        <p class="m-0 text-sm text-secondary">
          {{ callToActionLabel() }} → {{ wizard.landing_page_url() }}
        </p>
        @if (!hasLaunched()) {
          <button
            type="button"
            (click)="goToStep(3)"
            class="mt-auto cursor-pointer self-start text-sm font-semibold text-brand-studio hover:underline"
          >
            Edit
          </button>
        }
      </article>
    </div>

    <sa-estimate-banner [forecast]="forecast()" />

    @if (hasLaunched()) {
      <div class="flex flex-col gap-y-4">
        <dl
          class="m-0 flex flex-col gap-4 rounded-xl bg-brand-deep-purple p-6 text-white sm:flex-row"
        >
          <div class="flex-1">
            <dt class="text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">Ad ID</dt>
            <dd class="m-0 mt-1 text-lg font-bold">{{ launchedAdId() ?? 'Pending' }}</dd>
          </div>
          <div class="flex-1">
            <dt class="text-xs font-bold tracking-[0.08em] text-white/60 uppercase">Status</dt>
            <dd class="m-0 mt-1 text-lg font-bold text-green-400">In review with TikTok</dd>
          </div>
        </dl>

        <button type="button" (click)="goToDashboard()" class="primary-button self-start">
          Go to the ads dashboard
        </button>
      </div>
    } @else if (launchStage() === 'error') {
      <div
        role="alert"
        class="flex flex-col items-start gap-y-3 rounded-xl border border-destructive/30 bg-destructive/5 p-5"
      >
        <p class="m-0 text-sm font-semibold text-error">We could not launch this Spark ad</p>
        <p class="m-0 text-sm text-secondary">{{ launchError() }}</p>
        <button type="button" (click)="retryLaunch()" class="secondary-button">Try again</button>
      </div>
    } @else {
      <div class="flex flex-col gap-y-3">
        <div class="flex flex-row flex-wrap items-center gap-4">
          <button type="button" (click)="goToStep(3)" class="secondary-button">
            Back to Spark ad
          </button>
          <button
            type="button"
            (click)="launch()"
            [disabled]="isLaunching()"
            class="primary-button"
          >
            {{ isLaunching() ? 'Launching…' : 'Launch campaign' }}
          </button>
          <p class="m-0 text-sm text-secondary">
            Nothing spends until TikTok approves — usually under an hour.
          </p>
        </div>
        @if (isLaunching()) {
          <p role="status" class="m-0 text-sm font-semibold text-brand-studio">
            {{ launchProgressLabel() }}
          </p>
        }
      </div>
    }
  </section>
} @else {
  <!-- ── Steps 1–3 ────────────────────────────────────────────────────────── -->
  <div class="flex w-full flex-col gap-6 lg:flex-row lg:items-start">
    <!-- Step rail -->
    <nav aria-label="Builder steps" class="flex w-full shrink-0 flex-col gap-y-4 lg:w-64">
      <p class="m-0 text-xs font-bold tracking-[0.08em] text-secondary uppercase">Your 3 steps</p>

      <ol class="m-0 flex list-none flex-col gap-y-1.5 p-0">
        @for (definition of steps; track definition.step) {
          <li>
            <button
              type="button"
              (click)="goToStep(definition.step)"
              [attr.aria-current]="step() === definition.step ? 'step' : null"
              class="flex w-full cursor-pointer flex-row items-start gap-x-3 rounded-lg border p-3 text-left transition-colors"
              [class]="stepRowClass(definition.step)"
            >
              <span
                class="flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                [class]="stepDotClass(definition.step)"
                aria-hidden="true"
              >
                {{ stepStatus(definition.step) === 'done' ? '✓' : definition.step }}
              </span>
              <span class="flex min-w-0 flex-col gap-y-0.5">
                <span class="text-sm font-bold text-primary">{{ definition.label }}</span>
                <span class="text-xs text-secondary">{{ definition.sub }}</span>
              </span>
            </button>
          </li>
        }
      </ol>

      <div class="flex flex-col gap-y-2 rounded-xl bg-brand-deep-purple p-4 text-white">
        <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">
          First time here?
        </p>
        <p class="m-0 text-sm text-white/85">
          Work top to bottom. We block Continue until a step is genuinely ready to run — nothing
          goes live by accident.
        </p>
        <button
          type="button"
          (click)="openGlossary()"
          class="cursor-pointer self-start border-b border-white/40 text-sm font-semibold text-white"
        >
          Open the plain-English glossary
        </button>
      </div>
    </nav>

    <!-- Main column -->
    <div class="flex min-w-0 flex-1 flex-col gap-y-5">
      @switch (step()) {
        @case (1) {
          <header class="flex flex-col gap-y-1">
            <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">
              Step 1 of 3 · Campaign
            </p>
            <h1 class="m-0 text-2xl font-bold text-primary">What are we trying to achieve?</h1>
            <p class="m-0 max-w-xl text-sm text-secondary">
              A campaign is the top-level container. Pick one goal — TikTok optimises everything
              underneath it around that goal.
            </p>
          </header>

          <div
            class="flex flex-col gap-y-6 rounded-xl border border-border bg-card p-6 shadow-sm"
          >
            <div class="flex flex-col gap-y-2">
              <label for="spark-campaign-name" class="text-sm/6 font-medium text-primary">
                Campaign name
              </label>
              <input
                id="spark-campaign-name"
                type="text"
                class="input-primary"
                [value]="wizard.campaignName() ?? ''"
                (input)="setCampaignName($any($event.target).value)"
              />
              <p class="m-0 text-xs text-secondary">
                Tip: season · tactic · market. Future-you will thank present-you in reporting.
              </p>
            </div>

            <div class="h-px bg-border"></div>

            <fieldset class="m-0 flex flex-col gap-y-3 border-0 p-0">
              <div class="flex flex-row items-baseline justify-between gap-x-4">
                <legend class="text-sm/6 font-medium text-primary">Campaign objective</legend>
                <button
                  type="button"
                  (click)="openGlossary()"
                  class="cursor-pointer text-sm font-semibold text-brand-studio hover:underline"
                >
                  What does this mean?
                </button>
              </div>

              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                @for (objective of objectives; track objective.apiValue) {
                  <label
                    class="flex cursor-pointer flex-col gap-y-1.5 rounded-xl p-4 transition-colors"
                    [class]="objectiveCardClass(objective.apiValue)"
                  >
                    <span class="flex flex-row items-center justify-between gap-x-3">
                      <span class="text-base font-bold text-primary">{{ objective.label }}</span>
                      <input
                        type="radio"
                        name="spark-objective"
                        class="size-4 accent-[var(--brand-studio)]"
                        [value]="objective.apiValue"
                        [checked]="isObjectiveSelected(objective.apiValue)"
                        (change)="selectObjective(objective.apiValue, objective.label)"
                      />
                    </span>
                    <span class="text-sm text-secondary">{{ objective.help }}</span>
                  </label>
                }
              </div>
            </fieldset>

            <div class="h-px bg-border"></div>

            <!-- Whose ad account spends. Disclosed, not silent. -->
            @if (adAccount(); as account) {
              @if (account.usingShoutAccount) {
                <div class="flex flex-col gap-y-1 rounded-lg border border-border bg-muted/50 p-4">
                  <p class="m-0 text-sm font-bold text-primary">
                    Running through the SHOUT ad account
                  </p>
                  <p class="m-0 text-sm text-secondary">
                    You haven’t connected your own TikTok Business account yet, so this campaign
                    spends through SHOUT’s. Connect yours later to bring billing and reporting
                    in-house.
                  </p>
                </div>
              } @else if (!account.isResolved) {
                <div
                  class="flex flex-col gap-y-1 rounded-lg border border-amber-600/25 bg-amber-50 p-4 dark:bg-amber-900/20"
                >
                  <p class="m-0 text-sm font-bold text-amber-700 dark:text-amber-300">
                    No TikTok ad account available
                  </p>
                  <p class="m-0 text-sm text-secondary">{{ account.unresolvedReason }}</p>
                </div>
              }
            }

            <div class="flex flex-row items-start justify-between gap-x-6">
              <div class="flex max-w-md flex-col gap-y-1">
                <p class="m-0 text-sm/6 font-medium text-primary">Campaign budget optimisation</p>
                <p class="m-0 text-xs text-secondary">
                  Let TikTok move budget between this campaign's ad groups towards whatever is
                  working. Leave it off and each ad group spends its own budget.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                [attr.aria-checked]="wizard.budgetOptimizeOn()"
                (click)="toggleBudgetOptimise()"
                class="relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                [class]="wizard.budgetOptimizeOn() ? 'bg-brand-studio' : 'bg-muted'"
              >
                <span class="sr-only">Campaign budget optimisation</span>
                <span
                  aria-hidden="true"
                  class="ml-1 inline-block size-5 rounded-full bg-white transition-transform"
                  [class]="wizard.budgetOptimizeOn() ? 'translate-x-5' : 'translate-x-0'"
                ></span>
              </button>
            </div>
          </div>
        }

        @case (2) {
          <header class="flex flex-col gap-y-1">
            <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">
              Step 2 of 3 · Ad group
            </p>
            <h1 class="m-0 text-2xl font-bold text-primary">Who sees it, and for how much?</h1>
            <p class="m-0 max-w-xl text-sm text-secondary">
              One ad group is one audience plus one budget. Start broad — TikTok's delivery does
              better with room to move.
            </p>
          </header>

          <div class="flex flex-col gap-y-6 rounded-xl border border-border bg-card p-6 shadow-sm">
            <div class="flex flex-col gap-y-2">
              <label for="spark-adgroup-name" class="text-sm/6 font-medium text-primary">
                Ad group name
              </label>
              <input
                id="spark-adgroup-name"
                type="text"
                class="input-primary"
                [value]="wizard.adGroupName() ?? ''"
                (input)="setAdGroupName($any($event.target).value)"
              />
            </div>

            <div
              class="flex flex-row flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-brand-studio/20 bg-brand-studio/5 p-4"
            >
              <p class="m-0 text-sm font-bold text-brand-studio-dark">Placement</p>
              <p class="m-0 text-sm font-semibold text-primary">TikTok feed only</p>
              <p class="m-0 text-sm text-secondary">
                — Spark ads run on TikTok itself, so there is nothing to choose here.
              </p>
            </div>

            <div class="h-px bg-border"></div>

            <!-- Markets -->
            <div class="flex flex-col gap-y-2">
              <label for="spark-market" class="text-sm/6 font-medium text-primary">Markets</label>

              @if (wizard.location_ids().length > 0) {
                <ul class="m-0 flex list-none flex-row flex-wrap gap-2 p-0">
                  @for (locationId of wizard.location_ids(); track locationId) {
                    <li>
                      <button
                        type="button"
                        (click)="removeLocation(locationId)"
                        class="flex cursor-pointer flex-row items-center gap-x-1.5 rounded-full bg-brand-studio px-3 py-1.5 text-sm font-semibold text-white"
                      >
                        {{ locationLabel(locationId) }}
                        <span class="sr-only">Remove this market</span>
                        <span aria-hidden="true">×</span>
                      </button>
                    </li>
                  }
                </ul>
              }

              @if (marketsLoading()) {
                <p class="m-0 text-sm text-secondary">Loading the markets TikTok will deliver to…</p>
              } @else if (hasNoMarketOptions()) {
                <p class="m-0 text-sm text-amber-700 dark:text-amber-400">
                  TikTok returned no markets for this ad account and goal. Check the ad account is
                  approved for advertising, then try a different objective.
                </p>
              } @else {
                <select
                  id="spark-market"
                  class="input-primary"
                  (change)="onMarketSelected($event)"
                >
                  <option value="">+ Add a market…</option>
                  @for (market of addableMarkets(); track market.location_id) {
                    <option [value]="market.location_id">{{ market.name }}</option>
                  }
                </select>
                @if (wizard.location_ids().length === 0) {
                  <p class="m-0 text-xs text-secondary">
                    Pick at least one market. Start broad — TikTok's delivery does better with room
                    to move.
                  </p>
                }
              }
            </div>

            <!-- Age + gender -->
            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <fieldset class="m-0 flex flex-col gap-y-2 border-0 p-0">
                <legend class="text-sm/6 font-medium text-primary">Age</legend>
                <div class="flex flex-row gap-x-2">
                  @for (group of ageGroups; track group.value) {
                    <label
                      class="flex-1 cursor-pointer rounded-lg border py-2 text-center text-sm font-semibold transition-colors"
                      [class]="segmentClass(isAgeSelected(group.value))"
                    >
                      <input
                        type="checkbox"
                        class="sr-only"
                        [checked]="isAgeSelected(group.value)"
                        (change)="toggleAge(group.value)"
                      />
                      {{ group.label }}
                    </label>
                  }
                </div>
              </fieldset>

              <fieldset class="m-0 flex flex-col gap-y-2 border-0 p-0">
                <legend class="text-sm/6 font-medium text-primary">Gender</legend>
                <div class="flex flex-row gap-x-2">
                  @for (option of genders; track option.value) {
                    <label
                      class="flex-1 cursor-pointer rounded-lg border py-2 text-center text-sm font-semibold transition-colors"
                      [class]="segmentClass(wizard.gender() === option.value)"
                    >
                      <input
                        type="radio"
                        name="spark-gender"
                        class="sr-only"
                        [checked]="wizard.gender() === option.value"
                        (change)="setGender(option.value)"
                      />
                      {{ option.label }}
                    </label>
                  }
                </div>
              </fieldset>
            </div>

            <!-- Advanced targeting -->
            <div class="flex flex-col gap-y-3">
              <button
                type="button"
                (click)="toggleAdvanced()"
                [attr.aria-expanded]="advancedOpen()"
                class="flex cursor-pointer flex-row items-center justify-between gap-x-4 rounded-lg border border-border bg-muted/50 p-4 text-left"
              >
                <span class="flex flex-col gap-y-0.5">
                  <span class="text-sm font-bold text-primary">Advanced targeting</span>
                  <span class="text-xs text-secondary">
                    Bid strategy, frequency cap, optimisation event
                  </span>
                </span>
                <span class="text-sm font-bold text-brand-studio-dark">
                  {{ advancedOpen() ? 'Hide' : 'Show' }}
                </span>
              </button>

              @if (advancedOpen()) {
                <dl
                  class="m-0 grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/40 p-5 sm:grid-cols-3"
                >
                  <div class="flex flex-col gap-y-1">
                    <dt class="text-xs font-bold text-secondary">Optimisation goal</dt>
                    <dd class="m-0 text-sm font-semibold text-primary">
                      {{ wizard.optimization_goal() }}
                    </dd>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <dt class="text-xs font-bold text-secondary">Billing event</dt>
                    <dd class="m-0 text-sm font-semibold text-primary">
                      {{ wizard.billing_event() ?? 'Set automatically' }}
                    </dd>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <dt class="text-xs font-bold text-secondary">Bid strategy</dt>
                    <dd class="m-0 text-sm font-semibold text-primary">
                      {{
                        wizard.bid_type() === 'BID_TYPE_NO_BID' ? 'Lowest cost' : 'Custom bid'
                      }}
                    </dd>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <dt class="text-xs font-bold text-secondary">Frequency cap</dt>
                    <dd class="m-0 text-sm font-semibold text-primary">
                      {{ wizard.frequency() }} / {{ wizard.frequency_schedule() }} days
                    </dd>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <dt class="text-xs font-bold text-secondary">Devices</dt>
                    <dd class="m-0 text-sm font-semibold text-primary">
                      {{ wizard.operating_systems().join(', ') }}
                    </dd>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <dt class="text-xs font-bold text-secondary">Optimisation event</dt>
                    <dd class="m-0 text-sm font-semibold text-primary">
                      {{ wizard.optimization_event() ?? 'Not set' }}
                    </dd>
                  </div>
                </dl>
              }
            </div>

            <div class="h-px bg-border"></div>

            <!-- Budget & schedule -->
            <div class="flex flex-col gap-y-4">
              <div class="flex flex-row items-baseline justify-between gap-x-4">
                <p class="m-0 text-sm/6 font-medium text-primary">Budget &amp; schedule</p>
                <div class="flex flex-row gap-x-1 rounded-full bg-muted p-1">
                  <button
                    type="button"
                    (click)="setBudgetMode(true)"
                    class="cursor-pointer rounded-full px-3 py-1 text-sm font-semibold transition-colors"
                    [class]="
                      budgetIsDaily() ? 'bg-card text-primary shadow-sm' : 'text-secondary'
                    "
                  >
                    Daily
                  </button>
                  <button
                    type="button"
                    (click)="setBudgetMode(false)"
                    class="cursor-pointer rounded-full px-3 py-1 text-sm font-semibold transition-colors"
                    [class]="
                      !budgetIsDaily() ? 'bg-card text-primary shadow-sm' : 'text-secondary'
                    "
                  >
                    Lifetime
                  </button>
                </div>
              </div>

              <div class="flex flex-col gap-y-3 sm:flex-row sm:items-center sm:gap-x-5">
                <div
                  class="flex shrink-0 flex-row items-baseline gap-x-2 rounded-lg border border-border px-4 py-2.5"
                >
                  <p class="m-0 text-xl font-bold tabular-nums text-primary">
                    {{ wizard.budgetAdGroup() }}
                  </p>
                  <p class="m-0 text-sm font-semibold text-secondary">
                    {{ budgetIsDaily() ? 'per day' : 'lifetime' }}
                  </p>
                </div>

                <label class="flex-1">
                  <span class="sr-only">Ad group budget</span>
                  <input
                    type="range"
                    [min]="minBudget()"
                    [max]="budgetSliderMax"
                    [step]="budgetSliderStep"
                    [value]="wizard.budgetAdGroup()"
                    (input)="setBudget($any($event.target).value)"
                    class="w-full cursor-pointer accent-[var(--brand-studio)]"
                  />
                </label>
              </div>

              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="flex flex-col gap-y-1.5">
                  <label for="spark-start" class="text-xs font-bold text-secondary">Starts</label>
                  <input
                    id="spark-start"
                    type="datetime-local"
                    class="input-primary"
                    [value]="wizard.startDateAdGroup() ?? ''"
                    (input)="setStartDate($any($event.target).value)"
                  />
                </div>
                <div class="flex flex-col gap-y-1.5">
                  <label for="spark-end" class="text-xs font-bold text-secondary">Ends</label>
                  <input
                    id="spark-end"
                    type="datetime-local"
                    class="input-primary"
                    [value]="wizard.endDateAdGroup() ?? ''"
                    (input)="setEndDate($any($event.target).value)"
                  />
                </div>
              </div>
            </div>

            <sa-estimate-banner [forecast]="forecast()" />
          </div>
        }

        @case (3) {
          <header class="flex flex-col gap-y-1">
            <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">
              Step 3 of 3 · Spark ad
            </p>
            <h1 class="m-0 text-2xl font-bold text-primary">Pick the post you’re amplifying.</h1>
            <p class="m-0 max-w-xl text-sm text-secondary">
              A Spark ad boosts a real creator post from the creator’s own handle, keeping its likes
              and comments.
            </p>
          </header>

          <div class="flex flex-col gap-5 xl:flex-row xl:items-start">
            <div
              class="flex min-w-0 flex-1 flex-col gap-y-5 rounded-xl border border-border bg-card p-6 shadow-sm"
            >
              @if (usePastedCode()) {
                <!-- Take B: paste a TikTok auth code -->
                <div class="flex flex-row gap-x-5 border-b border-border">
                  <button
                    type="button"
                    (click)="showLibrary()"
                    class="cursor-pointer pb-3 text-base font-semibold text-secondary"
                  >
                    SHOUT library
                  </button>
                  <p
                    class="m-0 border-b-2 border-brand-studio pb-3 text-base font-bold text-primary"
                  >
                    Paste an auth code
                  </p>
                </div>

                <div class="flex flex-col gap-y-2">
                  <label for="spark-auth-code" class="text-sm/6 font-medium text-primary">
                    TikTok post auth code
                  </label>
                  <input
                    id="spark-auth-code"
                    type="text"
                    class="input-primary"
                    [value]="pastedCode()"
                    (input)="setPastedCode($any($event.target).value)"
                  />
                  <p class="m-0 text-xs text-secondary">
                    The creator generates this in TikTok → Creator Tools → Ad Settings. We only need
                    it once, and the post stays theirs.
                  </p>
                  <p class="m-0 text-xs text-secondary">
                    Applying a pasted code happens in the creator video library, where we can attach
                    it to the right post.
                  </p>
                </div>
              } @else {
                <!-- Take A: the cleared library -->
                <div
                  class="flex flex-col gap-y-3 sm:flex-row sm:items-end sm:justify-between sm:gap-x-4"
                >
                  <div class="flex flex-col gap-y-0.5">
                    <h2 class="m-0 text-base font-semibold text-primary">Creator posts</h2>
                    <p class="m-0 text-sm text-secondary">
                      Only posts the creator has authorised can carry budget.
                    </p>
                  </div>

                  <div class="flex shrink-0 flex-row gap-x-2">
                    <button
                      type="button"
                      (click)="setPostSort('topPerforming')"
                      class="cursor-pointer rounded-full px-3 py-1.5 text-sm font-semibold transition-colors"
                      [class]="
                        postSort() === 'topPerforming'
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'text-secondary'
                      "
                    >
                      Top performing
                    </button>
                    <button
                      type="button"
                      (click)="setPostSort('newest')"
                      class="cursor-pointer rounded-full px-3 py-1.5 text-sm font-semibold transition-colors"
                      [class]="
                        postSort() === 'newest'
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'text-secondary'
                      "
                    >
                      Newest
                    </button>
                  </div>
                </div>

                @if (libraryState() === 'loading') {
                  <div aria-busy="true" class="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    @for (card of [1, 2, 3, 4, 5, 6]; track card) {
                      <div
                        class="animate-pulse rounded-xl bg-gray-200 dark:bg-white/10"
                        style="aspect-ratio: 9 / 14"
                      ></div>
                    }
                  </div>
                } @else if (libraryState() === 'error') {
                  <p role="alert" class="m-0 text-sm text-error">
                    We could not load the creator posts.
                  </p>
                } @else if (selectablePosts().length === 0) {
                  <p class="m-0 text-sm text-secondary">
                    No creator posts are available for this brief yet.
                  </p>
                } @else {
                  <ul class="m-0 grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-3">
                    @for (entry of selectablePosts(); track entry.videoId) {
                      <li class="flex flex-col gap-1.5">
                        <button
                          type="button"
                          (click)="selectPost(entry)"
                          [disabled]="!postIsSelectable(entry)"
                          class="flex w-full flex-col overflow-hidden rounded-xl bg-card text-left transition-all disabled:cursor-not-allowed disabled:opacity-55"
                          [class]="postCardClass(entry)"
                        >
                          <span
                            class="relative block w-full overflow-hidden bg-muted"
                            style="aspect-ratio: 9 / 12"
                          >
                            @if (entry.video.video_info.thumbnail_url) {
                              <img
                                [src]="entry.video.video_info.thumbnail_url"
                                alt=""
                                aria-hidden="true"
                                loading="lazy"
                                class="h-full w-full object-cover"
                              />
                            }
                            <span
                              aria-hidden="true"
                              class="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"
                            ></span>
                            @if (isPostSelected(entry)) {
                              <span
                                class="absolute top-2 right-2 rounded-full bg-brand-studio px-2 py-0.5 text-xs font-bold text-white"
                              >
                                Selected
                              </span>
                            }
                            <span
                              class="absolute bottom-2 left-2.5 right-2.5 truncate text-sm font-bold text-white"
                            >
                              {{ entry.handle }}
                            </span>
                          </span>

                          <span class="flex flex-col gap-y-0.5 px-3 py-2.5">
                            @if (postIsSelectable(entry)) {
                              <span class="text-xs font-semibold text-success">
                                Authorised · ready to spark
                              </span>
                            } @else {
                              <span class="text-xs font-semibold text-amber-600 dark:text-amber-400">
                                {{ postBlockedNote(entry) }}
                              </span>
                            }
                          </span>
                        </button>
                        @if (canRequestPost(entry)) {
                          <button
                            type="button"
                            (click)="requestPostAuth(entry)"
                            [disabled]="requestingVideoId() !== undefined"
                            class="secondary-button w-full"
                          >
                            {{ requestLabel(entry) }}
                          </button>
                        }
                      </li>
                    }
                  </ul>
                }

                <div
                  class="flex flex-row flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-dashed border-border bg-muted/40 p-4"
                >
                  <p class="m-0 text-sm text-secondary">Amplifying a post from outside SHOUT?</p>
                  <button
                    type="button"
                    (click)="showPastedCode()"
                    class="cursor-pointer text-sm font-semibold text-brand-studio hover:underline"
                  >
                    Paste a TikTok auth code instead
                  </button>
                </div>
              }

              <div class="h-px bg-border"></div>

              <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <fieldset class="m-0 flex flex-col gap-y-2 border-0 p-0">
                  <legend class="text-sm/6 font-medium text-primary">Call to action</legend>
                  <div class="flex flex-row flex-wrap gap-2">
                    @for (cta of callsToAction; track cta.value) {
                      <label
                        class="cursor-pointer rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors"
                        [class]="ctaPillClass(cta.value)"
                      >
                        <input
                          type="radio"
                          name="spark-cta"
                          class="sr-only"
                          [checked]="wizard.call_to_action() === cta.value"
                          (change)="selectCallToAction(cta.value)"
                        />
                        {{ cta.label }}
                      </label>
                    }
                  </div>
                </fieldset>

                <div class="flex flex-col gap-y-2">
                  <label for="spark-destination" class="text-sm/6 font-medium text-primary">
                    Destination URL
                  </label>
                  <input
                    id="spark-destination"
                    type="url"
                    inputmode="url"
                    class="input-primary"
                    [value]="wizard.landing_page_url() ?? ''"
                    (input)="setLandingPageUrl($any($event.target).value)"
                  />
                  <p class="m-0 text-xs text-secondary">
                    We add campaign UTMs automatically — nothing to remember.
                  </p>
                </div>
              </div>
            </div>

            <div class="w-full shrink-0 xl:w-80">
              <sa-phone-preview
                [entry]="selectedVideo()"
                [callToActionLabel]="callToActionLabel()"
              />
            </div>
          </div>
        }
      }

      <!-- Footer actions -->
      <div class="flex flex-col gap-y-2 pt-2">
        <div class="flex flex-row flex-wrap items-center gap-3">
          @if (step() > 1) {
            <button type="button" (click)="back()" class="secondary-button">Back</button>
          }
          <button
            type="button"
            (click)="next()"
            [disabled]="!canContinue()"
            class="primary-button"
          >
            {{ continueLabel() }}
          </button>

          @if (canContinue()) {
            <p class="m-0 text-sm text-secondary">{{ readyNote() }}</p>
          }
        </div>

        <!-- Never a bare disabled button: always say what is missing -->
        @if (!canContinue()) {
          <p role="status" class="m-0 text-sm font-medium text-amber-700 dark:text-amber-400">
            {{ blockedReason() }}
          </p>
        }
      </div>
    </div>

    <!-- What you've built -->
    @if (showSummary()) {
      <aside
        class="flex w-full shrink-0 flex-col gap-y-4 rounded-xl border border-border bg-card p-5 shadow-sm lg:w-72"
        aria-label="What you've built"
      >
        <p class="m-0 text-xs font-bold tracking-[0.08em] text-secondary uppercase">
          What you’ve built
        </p>

        <div class="flex flex-col gap-y-4">
          <div class="flex flex-col gap-y-0.5 border-l-2 border-brand-studio pl-3">
            <p class="m-0 text-xs font-bold tracking-[0.06em] text-brand-studio-dark uppercase">
              Campaign
            </p>
            <p class="m-0 text-sm font-bold text-primary">
              {{ wizard.campaignName() ?? 'Not named yet' }}
            </p>
            <p class="m-0 text-xs text-secondary">{{ objectiveLabel() }}</p>
          </div>

          <div class="flex flex-col gap-y-0.5 border-l-2 border-brand-studio/35 pl-3">
            <p class="m-0 text-xs font-bold tracking-[0.06em] text-brand-studio-dark uppercase">
              Ad group
            </p>
            <p class="m-0 text-sm font-bold text-primary">
              {{ wizard.adGroupName() ?? 'Not named yet' }}
            </p>
            <p class="m-0 text-xs text-secondary">{{ summaryBudget() }}</p>
          </div>

          <div class="flex flex-col gap-y-0.5 border-l-2 border-brand-studio/35 pl-3">
            <p class="m-0 text-xs font-bold tracking-[0.06em] text-brand-studio-dark uppercase">
              Spark ad
            </p>
            <p class="m-0 text-sm font-bold text-primary">
              {{ selectedVideo()?.handle ?? 'No post chosen' }}
            </p>
            <p class="m-0 text-xs text-secondary">{{ callToActionLabel() }}</p>
          </div>
        </div>
      </aside>
    }
  </div>
}
